// Apparitions : ce qui entre dans l'écran se pose. Chaque élément qui bouge porte data-motion, un
// ou plusieurs effets séparés par des espaces ; data-motion-delai décale son départ (secondes).
//
// Les effets communs, ici :
//
//   lignes    le texte monte ligne par ligne, chacune sous son masque
//   monte     le bloc monte et apparaît
//   filet     le filet du haut (2 px) se trace de gauche à droite, le contenu suit
//   liste     les enfants arrivent l'un après l'autre (et leurs carrés, s'ils en ont)
//   compte    le nombre compte jusqu'à sa valeur ; data-motion-lie : au rythme du défilement (dans
//             un texte qui se surligne, au rythme de sa lecture)
//   chaine    la chaîne henri-ai se construit dans l'ordre du flux
//   surligne  le texte s'éclaire mot à mot, au rythme de la lecture
//   flux      le titre s'écrit mot à mot, comme une réponse qui arrive
//
// Les gestes, un par section (gestes.ts) : heros, soulignes, tiroir, palettes, palmares, grille,
// parc, paquet, couches, fiche, frappe. Les renvois (renvois.ts) soulignent ensemble les mots qui
// désignent la même chose.
//
// Sans JS, ou avant lui, tout est en place : les états de départ ne sont posés que par ce module.
// Au repos, aucun style ne reste en ligne et tout découpage de texte est défait : la page est
// celle du CSS, au pixel près. En mouvement réduit, rien ne se déplace : les apparitions sont de
// courts fondus, les compteurs et le surlignage sont à leur état final.

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { gestes } from './gestes.ts';
import {
  compteur,
  declencheur,
  decouper,
  delai,
  demonter,
  depart,
  echelons,
  fondu,
  poser,
  type Effet,
} from './outils.ts';
import { COURBE, DUREE, ECHELON, LIGNE_SOUS } from './reglages.ts';
import { monterRenvois } from './renvois.ts';

gsap.registerPlugin(ScrollTrigger);

/** Début de la lecture d'un texte : le surlignage part quand son haut passe ce seuil. */
const LECTURE = 'top 82%';

/**
 * Ce qu'un filet fait apparaître : ses descendants qui ne bougent pas d'eux-mêmes. On descend dans
 * un enfant qui contient un élément animé, pour ne jamais animer deux fois la même chose.
 */
function suiveurs(el: Element): Element[] {
  return [...el.children].flatMap((e) => {
    if (e.matches('[data-motion]')) return [];
    return e.querySelector('[data-motion]') ? suiveurs(e) : [e];
  });
}

const communs: Record<string, Effet> = {
  lignes(el, plein) {
    if (!plein) {
      fondu(el);
      return;
    }
    // Le texte se coupe en lignes au moment de jouer, dans la mise en page du moment, et retrouve
    // sa forme d'origine une fois posé : les titres équilibrent leurs lignes (text-wrap: balance),
    // ce qu'un découpage laissé en place figerait.
    gsap.set(el, { opacity: 0 });
    const jouer = () => {
      const s = decouper(el, { type: 'lines', mask: 'lines', linesClass: 'ligne' });
      // Le masque de chaque ligne déborde de sa boîte de 0,2 em pour laisser passer accents et
      // jambages (l'encre en dépasse d'au plus 0,14 em, au nom du héros) ; partie de LIGNE_SOUS,
      // la ligne reste cachée sous ce débord. clip-path et non overflow-clip-margin, que Safari
      // ne connaît pas : il rognerait l'accent et le jambage jusqu'à la fin, qui paraîtraient
      // d'un coup.
      gsap.set(s.masks, { overflow: 'visible', clipPath: 'inset(-0.2em)' });
      gsap.set(el, { clearProps: 'opacity' });
      gsap.from(s.lines, {
        yPercent: LIGNE_SOUS,
        duration: DUREE.entree,
        ease: COURBE.arrivee,
        stagger: ECHELON * 1.4,
        delay: delai(el),
        onComplete: () => {
          s.revert();
        },
      });
    };
    const { scrollTrigger: st } = declencheur(el);
    if (st) ScrollTrigger.create({ ...st, onEnter: jouer });
    else jouer();
  },

  monte(el, plein) {
    gsap.from(el, {
      ...depart(plein),
      duration: plein ? DUREE.entree : DUREE.etat,
      ease: COURBE.arrivee,
      delay: delai(el),
      ...declencheur(el),
    });
  },

  filet(el, plein) {
    const tl = gsap.timeline({ delay: delai(el), ...declencheur(el) });
    if (plein)
      tl.fromTo(
        el,
        { '--trace': 0 },
        { '--trace': 1, duration: DUREE.pose, ease: COURBE.arrivee, clearProps: '--trace' },
      );
    const contenu = suiveurs(el);
    if (contenu.length)
      tl.from(
        contenu,
        {
          ...depart(plein, 16),
          duration: plein ? DUREE.entree : DUREE.etat,
          ease: COURBE.arrivee,
          stagger: { amount: echelons(contenu.length) },
        },
        plein ? DUREE.pose * 0.2 : 0,
      );
    tl.call(() => {
      poser(el);
    });
  },

  liste(el, plein) {
    const items = [...el.children];
    const tl = gsap.timeline({ delay: delai(el), ...declencheur(el) });
    tl.from(items, {
      ...depart(plein, '0.5em'),
      duration: plein ? DUREE.entree : DUREE.etat,
      ease: COURBE.arrivee,
      stagger: { amount: echelons(items.length) },
    });
    // Les carrés pleins des listes (marques, listes du chapitre IA) se posent juste après leur mot.
    if (plein)
      tl.fromTo(
        items,
        { '--carre': 0 },
        {
          '--carre': 1,
          clearProps: '--carre',
          duration: DUREE.pose,
          ease: COURBE.arrivee,
          stagger: { amount: echelons(items.length) },
        },
        0.12,
      );
  },

  compte(el, plein) {
    if (!plein) return;
    const compte = compteur(el);
    if (!compte) return;
    const { cible, ecrire } = compte;
    const valeur = { v: 0 };
    ecrire(0);
    const lie = el.dataset.motionLie !== undefined;
    // Lié au défilement : le compte finit avant que le nombre n'atteigne le milieu de l'écran, là
    // où on le lit ; dans un texte qui se surligne, il suit le début de la lecture.
    const lecture = el.closest('[data-motion~="surligne"]');
    gsap.to(valeur, {
      v: cible,
      duration: lie ? 1 : DUREE.entree * 1.4,
      ease: lie ? 'none' : COURBE.douce,
      delay: lie ? 0 : delai(el),
      onUpdate: () => {
        ecrire(valeur.v);
      },
      ...(lie
        ? {
            scrollTrigger: lecture
              ? { trigger: lecture, start: LECTURE, end: 'top 48%', scrub: 0.4 }
              : { trigger: el, start: 'top 95%', end: 'top 60%', scrub: 0.4 },
          }
        : declencheur(el)),
    });
  },

  chaine(el, plein) {
    // Dans l'ordre du flux : des contenus du client jusqu'aux visiteurs, puis l'administration,
    // puis jolideploy, dessous.
    const ordre = [
      'bout--entree',
      'fleche--1',
      'outil--gen',
      'fleche--2',
      'site',
      'fleche--3',
      'outil--public',
      'fleche--4',
      'bout--sortie',
      'fleche--aller-retour',
      'outil--admin',
      'base',
    ];
    const tl = gsap.timeline({ ...declencheur(el, 'clamp(top 75%)') });
    for (const nom of ordre) {
      const n = el.querySelector(`.chaine__${nom}`);
      if (!n) continue;
      if (!nom.startsWith('fleche')) {
        tl.from(
          n,
          { ...depart(plein, 12), duration: plein ? 0.35 : DUREE.etat, ease: COURBE.arrivee },
          tl.duration() ? '-=0.2' : 0,
        );
        continue;
      }
      if (!plein) {
        tl.from(n, { opacity: 0, duration: DUREE.etat, clearProps: 'opacity' }, '<');
        continue;
      }
      // La flèche se trace dans son sens : à l'horizontale ou à la verticale selon la mise en page
      // (la chaîne se dresse sur téléphone). Le rognage déborde pour laisser passer la pointe.
      const r = n.getBoundingClientRect();
      const debut =
        r.width >= r.height ? 'inset(-12px 100% -12px 0)' : 'inset(-12px -12px 100% -12px)';
      tl.fromTo(
        n,
        { clipPath: debut },
        {
          clipPath: 'inset(-12px -12px -12px -12px)',
          duration: 0.18,
          ease: 'power2.inOut',
          clearProps: 'clipPath',
        },
        '-=0.04',
      );
    }
    tl.call(() => {
      poser(el);
    });
  },

  surligne(el, plein) {
    if (!plein) return;
    decouper(el, {
      type: 'words',
      wordsClass: 'mot',
      // Une fois lu jusqu'au bout, le texte retrouve sa forme d'origine, au pixel près.
      onSplit: (s) => {
        gsap.fromTo(
          s.words,
          { opacity: 0.18 },
          {
            opacity: 1,
            ease: 'none',
            stagger: 0.1,
            scrollTrigger: {
              trigger: el,
              start: LECTURE,
              end: 'bottom 52%',
              scrub: 0.3,
              onLeave: (st) => {
                st.kill(true);
                s.revert();
              },
            },
          },
        );
      },
    });
  },

  flux(el, plein) {
    if (!plein) {
      fondu(el);
      return;
    }
    decouper(el, {
      type: 'words',
      wordsClass: 'mot',
      onSplit: (s) => {
        // Caché dès la découpe, pas au déclenchement : le titre entre dans l'écran avant de
        // franchir son seuil, et s'y montrerait écrit, puis s'effacerait pour s'écrire.
        gsap.set(s.words, { opacity: 0 });
        const tl = gsap.timeline({ ...declencheur(el) });
        // Le curseur, un carré turquoise, suit le dernier mot écrit, clignote, puis s'en va.
        s.words.forEach((mot, i) => {
          tl.call(
            () => {
              s.words.forEach((m) => m.classList.toggle('mot--curseur', m === mot));
            },
            [],
            0.15 + i * 0.13,
          ).set(mot, { opacity: 1 }, 0.15 + i * 0.13);
        });
        const fin = s.words.at(-1);
        if (fin)
          tl.to(
            fin,
            { '--curseur': 0, duration: 0.01, repeat: 3, yoyo: true, repeatDelay: 0.36 },
            '+=0.3',
          );
        // Écrit, le titre retrouve sa forme d'origine, au pixel près.
        tl.call(() => {
          s.revert();
        });
      },
    });
  },
};

const effets: Record<string, Effet> = { ...communs, ...gestes };

/**
 * Pose les apparitions de la page : plein (mouvement) ou réduit (fondus). Rend la fonction qui
 * retire ce que les gestes laissent hors de GSAP (calques, écouteurs).
 */
export function appliquerApparitions(plein: boolean): () => void {
  for (const el of document.querySelectorAll<HTMLElement>('[data-motion]'))
    for (const nom of (el.dataset.motion ?? '').split(/\s+/)) effets[nom]?.(el, plein);
  if (plein) monterRenvois();
  return demonter;
}
